import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import CmsGallerySection from "../components/CmsGallerySection";
import { COMPANY } from "../data/siteData";
import { imageUrl } from "../lib/images";

export default function MedicinaltransportPage() {
  return (
    <div className="bg-[#070d18] text-slate-100 min-h-screen">
      <SEOHead
        title="Medicinaltransport"
        description="Specialiseret transport for medicinalindustrien med moderne L2H2 kølebiler, høj driftssikkerhed og langsigtede B2B-samarbejdsaftaler."
      />

      {/* Hero Header */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 border-b border-slate-800 bg-[#081224] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest">
              <i className="fa-solid fa-pills text-[11px]" aria-hidden="true" />
              <span>Særligt Fokusområde · Medicin & Apoteksvarer</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight font-display">
              Medicinaltransport med kompromisløs driftssikkerhed
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              KVC Express ApS tilbyder dedikerede transportløsninger for medicinalsektoren og apoteker i hele Danmark – inklusive Bornholm. Vores chauffører besidder lovpligtigt chaufføruddannelsesbevis og kurser i medicintransport.
            </p>

            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#0F766E] hover:bg-[#115E59] active:bg-[#134E4A] text-white font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#0F766E]/30 cursor-pointer"
              >
                <span>Drøft et pharma-samarbejde</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-200 hover:text-white font-semibold text-xs transition whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#2DD4BF]" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring: {COMPANY.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pharma Focus Pillars */}
      <section className="py-20 bg-slate-50 text-slate-900 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Image Feature */}
            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-xl">
                <img
                  src={imageUrl("/_assets/shutterstock-2487533651.jpg", "width=900,quality=85,format=auto")}
                  alt="KVC Express præcisionslogistik og digital temperaturstyring til medicinaltransport"
                  className="w-full h-80 sm:h-[450px] object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-4 rounded-xl border border-slate-200 text-xs shadow-lg">
                  <div className="text-slate-900 font-bold">Dedikeret Materiel til Faste Pharma-Aftaler</div>
                  <div className="text-slate-600 text-[11px] mt-0.5">Renlighed, præcision og højeste standard for køretøjerne</div>
                </div>
              </div>
            </div>

            {/* Right Text Content */}
            <div className="lg:col-span-6 space-y-6">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0F766E] font-bold">
                Pålidelighed & Uddannelse
              </div>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-950 tracking-tight font-display">
                Bygget til kravene i sundhedssektoren og apotekerne
              </h2>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                Transport af apoteksvarer og medicinalpræparater kræver mere end blot en bil på vejen. Det kræver biler, der er rene, driftssikre og forsynet med aktiv køl, samt uddannede chauffører, der forstår vigtigheden af ubrudt temperaturstyring.
              </p>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                Gennem vores samarbejder i distributionssektoren (herunder Gordon Delivery) varetager vi rutinemæssigt apoteksvarer og temperaturfølsomme produkter til modtagere i hele Danmark – inklusive Bornholm.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-sm">
                  <div className="text-[#0F766E] font-bold text-sm mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-snowflake text-[#0F766E]" aria-hidden="true" />
                    <span>Temperaturstyret</span>
                  </div>
                  <div className="text-xs text-slate-600 leading-snug">
                    Aktiv køl med fokus på stabil temperatur under transporten.
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-sm">
                  <div className="text-[#0F766E] font-bold text-sm mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-certificate text-[#0F766E]" aria-hidden="true" />
                    <span>Uddannede Chauffører</span>
                  </div>
                  <div className="text-xs text-slate-600 leading-snug">
                    Chaufføruddannelsesbevis og kurser i medicintransport.
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-sm">
                  <div className="text-[#0F766E] font-bold text-sm mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-map-location-dot text-[#0F766E]" aria-hidden="true" />
                    <span>Hele Danmark Inkl. Bornholm</span>
                  </div>
                  <div className="text-xs text-slate-600 leading-snug">
                    Pålidelig ruteafvikling på fastland såvel som til Bornholm.
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-sm">
                  <div className="text-[#0F766E] font-bold text-sm mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-charging-station text-[#0F766E]" aria-hidden="true" />
                    <span>Nye diesel- og elbiler</span>
                  </div>
                  <div className="text-xs text-slate-600 leading-snug">
                    Flåden omfatter nye diesel- og elbiler tilpasset medicinaldistribution.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <CmsGallerySection
        gallerySlug="medicinaltransport"
        title="Medicinaltransport & materiel"
        subtitle="Se vores moderne flåde fotograferet i professionelle pharma- og erhvervsomgivelser"
      />

      {/* Bottom CTA Block */}
      <section className="py-16 bg-[#050a14] border-t border-slate-800 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-white font-display">
            Søger I en stabil transportpartner til medicinalopgaver?
          </h3>
          <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
            Kontakt KVC Express ApS for en uforpligtende drøftelse af jeres krav til materiel og kørselsmønstre.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#0F766E] hover:bg-[#115E59] text-white font-bold text-xs uppercase tracking-wider transition shadow-xl shadow-[#0F766E]/40"
          >
            <span>Tal med os om medicinaltransport</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
